

import { useEffect, useState } from 'react';

import type { EnrichedEntity, EntityType, Relationship } from '@/lib/types';

import { useNovel } from '@/context/novel-context';
import { useFirebase } from '@/firebase';

export type RichRelationship = {
  id: string;
  rawRelationship: Relationship;
  description: string; // The description from the perspective of the source entity
  source: EnrichedEntity;
  target: EnrichedEntity;
};

export function useRelationships(novelId: string, entityId: string) {
  const { backend } = useFirebase();
  const { novel } = useNovel();
  const [relationships, setRelationships] = useState<RichRelationship[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!backend || !novel.id || !entityId || novel.id !== novelId) {
      setRelationships([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    const unsubscribe = backend.entities.subscribeRelationships(
      novel.ownerId,
      novel.id,
      novel.storageLayout,
      entityId,
      (rawRelationships) => {
        try {
          const results = rawRelationships.map((rel) => {
            const isSourceA = rel.entityAId === entityId;

            const sourceId = isSourceA ? rel.entityAId : rel.entityBId;
            const sourceType = (isSourceA ? rel.entityAType : rel.entityBType) as EntityType;
            const targetId = isSourceA ? rel.entityBId : rel.entityAId;
            const targetType = (isSourceA ? rel.entityBType : rel.entityAType) as EntityType;
            const description = isSourceA ? rel.forwardDescription : (rel.reverseDescription || `related to`);

            const sourceSummary = novel.summaries?.[sourceType]?.[sourceId];
            const targetSummary = novel.summaries?.[targetType]?.[targetId];

            if (!sourceSummary || !targetSummary) {
              console.warn(`Could not find summary for relationship ${rel.id}`);
              return null;
            }

            const sourceData: EnrichedEntity = {
              ...sourceSummary,
              id: sourceId,
              entityType: sourceType,
            };
            const targetData: EnrichedEntity = {
              ...targetSummary,
              id: targetId,
              entityType: targetType,
            };

            return {
              id: rel.id,
              rawRelationship: rel,
              description: description,
              source: sourceData,
              target: targetData,
            };
          });

          const richRels = results.filter((r): r is RichRelationship => r !== null);
          setRelationships(richRels);
        } catch (e) {
          setError(e as Error);
          console.error(e);
        } finally {
          setIsLoading(false);
        }
      },
      (err) => {
        setError(err);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [backend, novel, entityId, novelId]);

  return { relationships, isLoading, error };
}

